import React from 'react';
import {withSystem, Plugins, Component, Atom} from '@packages/systems/wfdl';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import {
  UsysUtilityPageConstraint,
  privacyText,
  communicationsText,
} from '../utils';
import {
  TListOfElement,
  EElement,
  ERecord,
  EEnum,
  EList,
  EText,
  EBoolean,
  ELiteral,
  ENumber,
  TUnion,
  TEnum,
  EUnion,
  TBoolean,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {
  USYS_PAGE_SETTINGS,
  signUpErrorStates,
  ERROR_STATE,
  SIGNUP_UI_ERROR_CODES,
  USYS_INPUT_SIGN_UP_IDS,
  RESERVED_USER_FIELDS,
  USYS_DATA_ATTRS,
} from '@packages/systems/users/constants';
import {
  EvaluatedNodeData,
  getChildren,
  getString,
  getBooleanOrElse,
  getInPojo,
} from '@packages/utilities/data-utils';

const getFormStatus = (data: EvaluatedNodeData) => {
  const unionText = getString(getInPojo(data, ['temp', 'tag']));
  return unionText === 'Verification' ? 'verification' : 'default';
};

const UserSignUpFormWrapper = Atom.createRender(
  ({attributes, classes, generateAttributes, data, renderChild, context}) => {
    const newContext = context
      .set('formStatus', getFormStatus(data))
      .set(
        'includeNameField',
        getBooleanOrElse(getInPojo(data, ['includeNameField']), true)
      )
      .set(
        'formState',
        getString(getInPojo(data, ['temp', 'val'])) ?? 'normal'
      );
    return (
      <div {...generateAttributes(attributes, classes)}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, newContext)
        )}
      </div>
    );
  }
);

const config = createIntegratedAtom({
  constraints: {
    descendants: [
      {
        is: 'UserSignUpForm',
        rule: 'ExactlyOne',
      },
      {
        is: 'UserFormErrorState',
        rule: 'ExactlyOne',
      },
      {
        is: 'UserFormSuccessState',
        rule: 'ExactlyOne',
      },
      {
        is: 'UserSignUpVerificationMessage',
        rule: 'ExactlyOne',
      },
      /*
        UserSignUpRedirectWrapper lives in UserFormSuccessState. Since
        UserFormSuccessState is reused in several forms, the
        UserSignUpRedirectWrapper constraint is added here. If
        UserFormSuccessState is ever removed from UserSignUpFormWrapper,
        UserSignUpRedirectWrapper descendant should also be removed here.
       */
      {
        is: 'UserSignUpRedirectWrapper',
        rule: 'ExactlyOne',
      },
    ],
    page: [UsysUtilityPageConstraint],
  },
  displayName: 'Sign Up Form Block',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Form'), iconName('FormBlock')),
  },
  render: UserSignUpFormWrapper,
  showEmpty: true,
  fields: {
    temp: withSystem(
      {label: 'Sign Up Status', isFullWidth: true},
      TUnion({
        Default: withSystem(
          {label: 'State', defaultValue: 'normal', isFullWidth: true},
          TEnum({
            normal: 'Normal',
            success: 'Success',
            error: 'Error',
          })
        ),
        Verification: withSystem(
          {label: 'State', defaultValue: 'normal', isFullWidth: true},
          TEnum({
            normal: 'Normal',
            error: 'Error',
          })
        ),
      })
    ),
    // fixme: this was previously public, removing it would cause a legacy UserFormNameInput, that was previously hidden by the user, to reappear on old sites a migration would fix this
    includeNameField: withSystem(
      {
        private: true,
        defaultValue: false,
      },
      TBoolean
    ),
    children: TListOfElement,
  },
  style: {
    base: {
      main: {
        noPseudo: {
          position: 'relative',
          marginBottom: px(0),
          paddingLeft: px(20),
          paddingRight: px(20),
        },
      },
    },
  },
});

export default config;

export const UserSignUpFormPreset = EElement({
  id: 'User Sign Up Form Wrapper',
  type: qualify(Plugins.name('Users'), Component.name('UserSignUpFormWrapper')),
  data: ERecord({
    temp: EUnion({
      tag: 'Default',
      value: EEnum('normal'),
    }),
    includeNameField: EBoolean(false),
    children: EList([
      EElement({
        id: 'User Sign Up Success Message',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserFormSuccessState')
        ),
        data: ERecord({
          children: EList([
            EElement({
              id: 'Header',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormHeader')
              ),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'Image',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Image')
                    ),
                    data: ERecord({
                      img: ELiteral({
                        name: qualify(Plugins.name('Asset'), typeName('Image')),
                        value: {},
                      }),
                      attr: ERecord({
                        src: EText(
                          'https://d3e54v103j8qbb.cloudfront.net/img/thumbs-up-72.cbcaec93bc.svg'
                        ),
                        loading: EEnum('lazy'),
                        width: EText('auto'),
                        height: EText('auto'),
                        alt: EText('__wf_reserved_decorative'),
                      }),
                      temp: ERecord({
                        img: ERecord({
                          src: EText(''),
                        }),
                      }),
                      srcsetDisabled: EBoolean(false),
                      sizes: EList([]),
                    }),
                  }),
                  EElement({
                    id: 'User Form Page Wrap Heading',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Heading')
                    ),
                    data: ERecord({
                      tag: EEnum('h2'),
                      children: EList([
                        EElement({
                          id: 'User Form Page Wrap String',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Account activated'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Page Wrap Paragraph',
              type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'User Form Page Wrap String 2',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText(
                      'Your account was created successfully. You will be redirected shortly.'
                    ),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Sign Up Redirect',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserSignUpRedirectWrapper')
              ),
              data: ERecord({
                attr: ERecord({
                  [USYS_DATA_ATTRS.redirectUrl]: EText(''),
                }),
                children: EList([
                  EElement({
                    id: 'User Sign Up Redirect Link',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Link')
                    ),
                    data: ERecord({
                      button: EBoolean(false),
                      block: EText('inline'),
                      eventIds: EList([]),
                      link: ELiteral({
                        name: qualify(Plugins.name('Basic'), typeName('Link')),
                        value: {
                          mode: 'external',
                          url: '/',
                        },
                      }),
                      children: EList([
                        EElement({
                          id: 'User Sign Up Redirect Paragraph String 2',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('If nothing happens, click here.'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
      EElement({
        id: 'User Sign Up Form',
        type: qualify(Plugins.name('Users'), Component.name('UserSignUpForm')),
        data: ERecord({
          type: EText('sign-up'),
          temp: EList([]),
          children: EList([
            EElement({
              id: 'User Form Header',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormHeader')
              ),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'User Form Page Wrap Heading 2',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Heading')
                    ),
                    data: ERecord({
                      tag: EEnum('h2'),
                      children: EList([
                        EElement({
                          id: 'User Form Page Wrap String 4',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Sign up'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Block Label',
              type: qualify(
                Plugins.name('Form'),
                Component.name('FormBlockLabel')
              ),
              data: ERecord({
                form: ERecord({
                  type: EText('label'),
                  passwordPage: EBoolean(false),
                }),
                attr: ERecord({
                  for: EText('Email'),
                }),
                children: EList([
                  EElement({
                    id: 'User Form Page Wrap String 5',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('Email'),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Email Input',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormEmailInput')
              ),
              data: ERecord({
                attr: ERecord({
                  autofocus: EBoolean(false),
                  maxlength: ENumber(256),
                  placeholder: EText(''),
                  disabled: EBoolean(false),
                  name: EText('Email'),
                  id: EText(USYS_INPUT_SIGN_UP_IDS.email),
                }),
              }),
            }),
            EElement({
              id: 'User Form Block Label 2',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormBlockLabel')
              ),
              data: ERecord({
                users: ERecord({
                  field: EText(RESERVED_USER_FIELDS.name),
                }),
                attr: ERecord({
                  for: EText('Name'),
                }),
                children: EList([
                  EElement({
                    id: 'User Form Page Wrap String 6',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('Name'),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'Name Input',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormTextInput')
              ),
              data: ERecord({
                users: ERecord({
                  context: EEnum('signUp'),
                  field: EText(RESERVED_USER_FIELDS.name),
                  fieldType: EText(''),
                }),
                attr: ERecord({
                  autofocus: EBoolean(false),
                  maxlength: ENumber(256),
                  type: EEnum('text'),
                  name: EText(''),
                  fieldType: EText(USYS_DATA_ATTRS.fieldType),
                  id: EText(USYS_INPUT_SIGN_UP_IDS.name),
                  disabled: EBoolean(false),
                  required: EBoolean(true),
                  'data-name': EText(''),
                  placeholder: EText(''),
                }),
                form: ERecord({
                  type: EText('input'),
                  name: EText('name'),
                }),
              }),
            }),
            EElement({
              id: 'User Form Block Label 3',
              type: qualify(
                Plugins.name('Form'),
                Component.name('FormBlockLabel')
              ),
              data: ERecord({
                form: ERecord({
                  type: EText('label'),
                  passwordPage: EBoolean(false),
                }),
                attr: ERecord({
                  for: EText('Password'),
                }),
                children: EList([
                  EElement({
                    id: 'User Form Page Wrap String 7',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('Password'),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Password Input',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormPasswordInput')
              ),
              data: ERecord({
                attr: ERecord({
                  'data-name': EText(''),
                  autofocus: EBoolean(false),
                  maxlength: ENumber(256),
                  placeholder: EText(''),
                  disabled: EBoolean(false),
                  name: EText('Password'),
                  id: EText(USYS_INPUT_SIGN_UP_IDS.password),
                }),
              }),
            }),
            EElement({
              id: 'Privacy Checkbox Wrapper',
              type: qualify(
                Plugins.name('Form'),
                Component.name('FormCheckboxWrapper')
              ),
              data: ERecord({
                form: ERecord({
                  type: EText('checkbox'),
                }),
                children: EList([
                  EElement({
                    id: 'Privacy checkbox',
                    type: qualify(
                      Plugins.name('Users'),
                      Component.name('UserFormCheckboxInput')
                    ),
                    data: ERecord({
                      users: ERecord({
                        context: EEnum('signUp'),
                        field: EText(RESERVED_USER_FIELDS.acceptPrivacy),
                        fieldType: EText(''),
                      }),
                      attr: ERecord({
                        name: EText(''),
                        'data-name': EText(''),
                        placeholder: EText(''),
                        type: EText('checkbox'),
                        id: EText(USYS_INPUT_SIGN_UP_IDS.acceptPrivacy),
                        checked: EBoolean(false),
                        required: EBoolean(true),
                      }),
                      inputType: EEnum('default'),
                      form: ERecord({
                        type: EText('checkbox-input'),
                        name: EText(''),
                      }),
                    }),
                  }),
                  EElement({
                    id: 'Checkbox Label 1',
                    type: qualify(
                      Plugins.name('Form'),
                      Component.name('FormInlineLabel')
                    ),
                    data: ERecord({
                      form: ERecord({
                        type: EText('checkbox-label'),
                      }),
                      children: privacyText,
                    }),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'Communications Checkbox Wrapper',
              type: qualify(
                Plugins.name('Form'),
                Component.name('FormCheckboxWrapper')
              ),
              data: ERecord({
                form: ERecord({
                  type: EText('checkbox'),
                }),
                children: EList([
                  EElement({
                    id: 'Communications Checkbox',
                    type: qualify(
                      Plugins.name('Users'),
                      Component.name('UserFormCheckboxInput')
                    ),
                    data: ERecord({
                      users: ERecord({
                        context: EEnum('signUp'),
                        field: EText(RESERVED_USER_FIELDS.acceptCommunications),
                        fieldType: EText(''),
                      }),
                      attr: ERecord({
                        type: EText('checkbox'),
                        id: EText(USYS_INPUT_SIGN_UP_IDS.acceptCommunications),
                        checked: EBoolean(false),
                        required: EBoolean(true),
                        name: EText(''),
                        'data-name': EText(''),
                        placeholder: EText(''),
                      }),
                      inputType: EEnum('default'),
                      form: ERecord({
                        type: EText('checkbox-input'),
                        name: EText(''),
                      }),
                    }),
                  }),
                  EElement({
                    id: 'Form Inline Label',
                    type: qualify(
                      Plugins.name('Form'),
                      Component.name('FormInlineLabel')
                    ),
                    data: ERecord({
                      form: ERecord({
                        type: EText('checkbox-label'),
                      }),
                      children: communicationsText,
                    }),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Button',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormButton')
              ),
              data: ERecord({
                attr: ERecord({
                  type: EText('submit'),
                  value: EText('Sign Up'),
                  'data-wait': EText('Please wait...'),
                }),
              }),
            }),
            EElement({
              id: 'User Form Footer',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormFooter')
              ),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'User Form Page Wrap Span',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Span')
                    ),
                    data: ERecord({
                      children: EList([
                        EElement({
                          id: 'User Form Page Wrap String 8',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Already have an account?'),
                        }),
                      ]),
                    }),
                  }),
                  EElement({
                    id: 'User Form Page Wrap Link 2',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Link')
                    ),
                    data: ERecord({
                      button: EBoolean(false),
                      block: EText('inline'),
                      eventIds: EList([]),
                      link: ELiteral({
                        name: qualify(Plugins.name('Basic'), typeName('Link')),
                        value: {
                          mode: 'external',
                          url: `/${USYS_PAGE_SETTINGS.login.slug}`,
                        },
                      }),
                      children: EList([
                        EElement({
                          id: 'User Form Page Wrap String 9',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Log In'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
      EElement({
        id: 'User Signup Error State',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserFormErrorState')
        ),
        data: ERecord({
          users: ERecord({
            type: EText(ERROR_STATE.SIGNUP),
          }),
          children: EList([
            EElement({
              id: 'User Signup Error Msg',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserErrorMsg')
              ),
              data: ERecord({
                users: ERecord({
                  type: EText(ERROR_STATE.SIGNUP),
                }),
                temp: ERecord({
                  index: ENumber(0),
                }),
                errors: EList([
                  ERecord({
                    id: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.GENERAL_ERROR].id
                    ),
                    name: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.GENERAL_ERROR]
                        .name
                    ),
                    copy: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.GENERAL_ERROR]
                        .copy
                    ),
                  }),
                  ERecord({
                    id: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.NOT_ALLOWED].id
                    ),
                    name: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.NOT_ALLOWED].name
                    ),
                    copy: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.NOT_ALLOWED].copy
                    ),
                  }),
                  ERecord({
                    id: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.INVALID_EMAIL].id
                    ),
                    name: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.INVALID_EMAIL]
                        .name
                    ),
                    copy: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.INVALID_EMAIL]
                        .copy
                    ),
                  }),
                  ERecord({
                    id: EText(
                      signUpErrorStates[
                        SIGNUP_UI_ERROR_CODES.EMAIL_ALREADY_EXIST
                      ].id
                    ),
                    name: EText(
                      signUpErrorStates[
                        SIGNUP_UI_ERROR_CODES.EMAIL_ALREADY_EXIST
                      ].name
                    ),
                    copy: EText(
                      signUpErrorStates[
                        SIGNUP_UI_ERROR_CODES.EMAIL_ALREADY_EXIST
                      ].copy
                    ),
                  }),
                  ERecord({
                    id: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.USE_INVITE_EMAIL]
                        .id
                    ),
                    name: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.USE_INVITE_EMAIL]
                        .name
                    ),
                    copy: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.USE_INVITE_EMAIL]
                        .copy
                    ),
                  }),
                  ERecord({
                    id: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.INVALID_PASSWORD]
                        .id
                    ),
                    name: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.INVALID_PASSWORD]
                        .name
                    ),
                    copy: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.INVALID_PASSWORD]
                        .copy
                    ),
                  }),
                  ERecord({
                    id: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.NOT_VERIFIED].id
                    ),
                    name: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.NOT_VERIFIED].name
                    ),
                    copy: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.NOT_VERIFIED].copy
                    ),
                  }),
                  ERecord({
                    id: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.EXPIRED_TOKEN].id
                    ),
                    name: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.EXPIRED_TOKEN]
                        .name
                    ),
                    copy: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.EXPIRED_TOKEN]
                        .copy
                    ),
                  }),
                  ERecord({
                    id: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.VALIDATION_FAILED]
                        .id
                    ),
                    name: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.VALIDATION_FAILED]
                        .name
                    ),
                    copy: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.VALIDATION_FAILED]
                        .copy
                    ),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
      EElement({
        id: 'User Sign Up Verification Message',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserSignUpVerificationMessage')
        ),
        data: ERecord({
          temp: ERecord({
            state: EEnum('USER_SIGNUP_VERIFICATION'),
          }),
          children: EList([
            EElement({
              id: 'User Form Header',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormHeader')
              ),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'Image',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Image')
                    ),
                    data: ERecord({
                      img: ELiteral({
                        name: qualify(Plugins.name('Asset'), typeName('Image')),
                        value: {},
                      }),
                      attr: ERecord({
                        src: EText(
                          'https://d3e54v103j8qbb.cloudfront.net/img/email-72.67fa6be437.svg'
                        ),
                        loading: EEnum('lazy'),
                        width: EText('auto'),
                        height: EText('auto'),
                        alt: EText('__wf_reserved_decorative'),
                      }),
                      temp: ERecord({
                        img: ERecord({
                          src: EText(''),
                        }),
                      }),
                      srcsetDisabled: EBoolean(false),
                      sizes: EList([]),
                    }),
                  }),
                  EElement({
                    id: 'Body Heading',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Heading')
                    ),
                    data: ERecord({
                      tag: EEnum('h2'),
                      children: EList([
                        EElement({
                          id: 'Body String',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Verification Required'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'Body Paragraph',
              type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'Body String 2',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText(
                      'Account verification required. Please check your email to find your unique verification link.'
                    ),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
    ]),
  }),
});
